import { useState } from "react";
import { toast } from "sonner";
import { MoreHorizontal, MailCheck, KeyRound, UserX, ShieldCheck } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { BrandButton } from "@/components/PageHeader";
import { api, errMsg } from "@/lib/api";

const COPY = {
  verify: { title: "Verify account without code", confirmLabel: "Verify account", note: (m) => `${m.name}'s account (${m.email}) will be marked as confirmed without the 6-digit code. Only do this after checking their identity.` },
  reset: { title: "Reset member password", confirmLabel: "Reset password", note: (m) => `${m.name}'s current password stops working and they are signed out. A link to choose a new password (valid 24 hours) is emailed to ${m.email}.` },
  remove: { title: "Remove member account", confirmLabel: "Remove account", note: (m) => `${m.name}'s login is deleted and they are signed out. Their registrations, payments and receipts stay in the records. They can create a new account later.` },
};

const ReasonDialog = ({ m, kind, onClose, onDone }) => {
  const [reason, setReason] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const c = COPY[kind];
  const go = async () => {
    if (reason.trim().length < 3) { toast.error("Please give the reason"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/member-accounts/${m.id}/${{ reset: "reset-password", remove: "remove", verify: "verify" }[kind]}`, { reason, confirm });
      toast.success(kind === "reset" ? `Password reset. Link emailed to ${r.data.email}` : kind === "verify" ? "Account verified. The member has been notified by email and SMS" : `Account removed. ${r.data.registrations_kept} registration(s) kept`);
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={(v) => !v && !busy && onClose()}>
      <DialogContent data-testid={`member-${kind}-dialog`}>
        <DialogHeader><DialogTitle>{c.title}</DialogTitle><DialogDescription>{c.note(m)}</DialogDescription></DialogHeader>
        <div className="text-xs font-semibold -mb-2">Reason</div>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} placeholder="Reason" aria-label="Reason" className="min-h-[80px]" data-testid={`member-${kind}-reason`} />
        {kind === "remove" && <div><div className="text-xs font-semibold mb-1">Type <span className="font-mono">{m.email}</span> to confirm</div>
          <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} className="h-10" data-testid="member-remove-confirm-input" /></div>}
        <div className="flex justify-end gap-2">
          <BrandButton variant="outline" onClick={onClose} disabled={busy} data-testid={`member-${kind}-cancel`}>Cancel</BrandButton>
          <BrandButton onClick={go} disabled={busy || (kind === "remove" && confirm.trim().toLowerCase() !== m.email.toLowerCase())} data-testid={`member-${kind}-confirm`}>{busy ? "Working…" : c.confirmLabel}</BrandButton>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const MemberActions = ({ m, superAdmin, onChanged }) => {
  const [kind, setKind] = useState(null);
  const resend = async () => {
    try {
      const r = await api.post(`/member-accounts/${m.id}/resend-verification`);
      toast.success(`Confirmation link and code sent to ${r.data.email}`);
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (m.email_verified && !superAdmin) { return null; }
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild><button type="button" className="h-9 w-9 inline-grid place-items-center rounded-md border border-gray-300 hover:border-[#E60000]" aria-label="More actions" data-testid={`member-account-more-${m.id}`}><MoreHorizontal className="w-4 h-4" /></button></DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {!m.email_verified && <DropdownMenuItem onSelect={resend} data-testid={`member-account-resend-${m.id}`}><MailCheck className="w-4 h-4 mr-2" />Resend confirmation link & code</DropdownMenuItem>}
          {superAdmin && !m.email_verified && <DropdownMenuItem onSelect={() => setKind("verify")} data-testid={`member-account-verify-${m.id}`}><ShieldCheck className="w-4 h-4 mr-2" />Verify account (no code)</DropdownMenuItem>}
          {superAdmin && <DropdownMenuItem onSelect={() => setKind("reset")} data-testid={`member-account-reset-${m.id}`}><KeyRound className="w-4 h-4 mr-2" />Reset password</DropdownMenuItem>}
          {superAdmin && <DropdownMenuItem onSelect={() => setKind("remove")} className="text-[#B30000]" data-testid={`member-account-remove-${m.id}`}><UserX className="w-4 h-4 mr-2" />Remove account</DropdownMenuItem>}
        </DropdownMenuContent>
      </DropdownMenu>
      {kind && <ReasonDialog m={m} kind={kind} onClose={() => setKind(null)} onDone={() => { setKind(null); onChanged(); }} />}
    </>
  );
};
